import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, Eye, EyeOff, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InviteAcceptPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/admin/invite/${token}`)
      .then(r => { if (alive) setInvite(r.data); })
      .catch(e => { if (alive) setError(e.response?.data?.detail || "Ο σύνδεσμος δεν είναι έγκυρος"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Ελάχιστο 8 χαρακτήρες"); return; }
    if (password !== confirm) { setError("Οι κωδικοί δεν ταιριάζουν"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/invite/${token}/accept`, { password });
      setDone(true);
      setTimeout(() => navigate("/admin/login"), 2500);
    } catch (e) {
      setError(e.response?.data?.detail || "Σφάλμα");
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-zinc-400">Φόρτωση...</div>;

  if (!invite && error) return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <div className="bg-[#111] border border-red-500/30 rounded-xl p-8 max-w-md text-center">
        <h1 className="font-['Bebas_Neue'] text-3xl text-red-400 mb-2">Σφάλμα</h1>
        <p className="text-sm text-zinc-400">{error}</p>
      </div>
    </div>
  );

  if (done) return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <div className="bg-[#111] border border-green-500/30 rounded-xl p-8 max-w-md text-center" data-testid="invite-success">
        <CheckCircle2 size={48} className="text-green-400 mx-auto mb-3" />
        <h1 className="font-['Bebas_Neue'] text-3xl text-white mb-2">Ολοκληρώθηκε!</h1>
        <p className="text-sm text-zinc-400">Ο λογαριασμός σου ενεργοποιήθηκε. Ανακατεύθυνση στη σύνδεση...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <div className="bg-[#111] border border-[#262626] rounded-xl p-8 max-w-md w-full" data-testid="invite-page">
        <div className="text-center mb-6">
          <Lock size={36} className="text-[#F5A623] mx-auto mb-3" />
          <h1 className="font-['Bebas_Neue'] text-3xl text-white tracking-wide">Καλώς ήρθες, {invite.name || invite.username}!</h1>
          <p className="text-xs text-zinc-500 mt-1">Σε καλέσαμε ως <strong className="text-[#F5A623]">{invite.role_label}</strong>. Όρισε τον κωδικό σου για να ενεργοποιήσεις τον λογαριασμό.</p>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Username</label>
            <input value={invite.username} disabled className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-2.5 text-sm text-zinc-500" />
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Νέος Κωδικός (≥8 χαρακτήρες)</label>
            <div className="relative">
              <input type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
                className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-2.5 pr-10 text-sm text-white focus:border-[#F5A623] focus:outline-none"
                data-testid="invite-password" required />
              <button type="button" onClick={() => setShowPw(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Επιβεβαίωση Κωδικού</label>
            <input type={showPw ? "text" : "password"} value={confirm} onChange={e => setConfirm(e.target.value)}
              className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-2.5 text-sm text-white focus:border-[#F5A623] focus:outline-none"
              data-testid="invite-confirm" required />
          </div>
          {error && <p className="text-xs text-red-400" data-testid="invite-error">{error}</p>}
          <button type="submit" disabled={submitting} className="w-full bg-[#F5A623] text-black font-semibold py-2.5 rounded-lg hover:bg-[#FF8C00] transition-colors disabled:opacity-50" data-testid="invite-submit">
            {submitting ? "Δημιουργία..." : "Ενεργοποίηση Λογαριασμού"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default InviteAcceptPage;
